import type { Collector } from '@/types/odoo';

export interface BlokGroup {
  blok: string;
  verdiepingen: VerdiepingGroup[];
  totalCollectors: number;
}

export interface VerdiepingGroup {
  verdieping: string;
  collectors: Collector[];
}

export const FOTO_LABELS: Record<string, string> = {
  Geen_Fotos: 'Geen Fotos',
  Fotos_Geupload: 'Geupload',
  Fotos_Goedgekeurd: 'Goedgekeurd',
  Fotos_Gevorderd: 'Gevorderd',
};

// Derived photo-status label.
// state='1_done' (Gereed in Odoo) is the canonical "this collector is vordered"
// marker — there is no Fotos_Gevorderd value in the Odoo enum, so we surface
// 'Gevorderd' here based on the task state instead.
// Returns null when there's no status at all; callers pick their own fallback.
export function getCollectorFotoLabel(collector: { state?: string; x_studio_fotos_uitvoering: string | false }): string | null {
  if (collector.state === '1_done') return 'Gevorderd';
  if (!collector.x_studio_fotos_uitvoering) return null;
  return FOTO_LABELS[collector.x_studio_fotos_uitvoering] ?? collector.x_studio_fotos_uitvoering;
}

export function buildBlokGroups(collectors: Collector[]): BlokGroup[] {
  const blokMap = new Map<string, Map<string, Collector[]>>();

  for (const collector of collectors) {
    const blok = (collector.x_studio_blok && collector.x_studio_blok[1]) || 'Hoofdgebouw';
    const verdieping = collector.x_studio_verdieping || 'Onbekend';

    let verdiepingMap = blokMap.get(blok);
    if (!verdiepingMap) {
      verdiepingMap = new Map();
      blokMap.set(blok, verdiepingMap);
    }

    let list = verdiepingMap.get(verdieping);
    if (!list) {
      list = [];
      verdiepingMap.set(verdieping, list);
    }
    list.push(collector);
  }

  const groups: BlokGroup[] = [];
  for (const [blok, verdiepingMap] of blokMap) {
    const verdiepingen: VerdiepingGroup[] = [];
    let totalCollectors = 0;

    for (const [verdieping, cols] of verdiepingMap) {
      cols.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
      verdiepingen.push({ verdieping, collectors: cols });
      totalCollectors += cols.length;
    }

    verdiepingen.sort((a, b) => a.verdieping.localeCompare(b.verdieping));
    groups.push({ blok, verdiepingen, totalCollectors });
  }

  groups.sort((a, b) => a.blok.localeCompare(b.blok));
  return groups;
}

/**
 * Option text for the materiaallijst pickers: filename (with extension) plus
 * the moment the lijst was uploaded, e.g.
 * "P 25315133 - Vandevyvere Aalst - Sfinx - CSV.csv · 13-07-2026 14:32".
 * The timestamp is stored as UTC and rendered in Brussels time.
 */
export function formatPickingOption(picking: { label: string; uploaded_at?: string | null }): string {
  const when = formatUploadedAt(picking.uploaded_at);
  return when ? `${picking.label} · ${when}` : picking.label;
}

export function formatUploadedAt(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // nl-NL, not nl-BE: Belgian Dutch formats dates with slashes (13/07/2026);
  // the portal shows dd-mm-yyyy everywhere else.
  return new Intl.DateTimeFormat('nl-NL', {
    timeZone: 'Europe/Brussels',
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(d).replace(', ', ' ');
}

export function formatShortDate(dateStr: string | false): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('nl-BE', { day: 'numeric', month: 'short' });
}

export function getCollectorMeta(collector: Collector, projectName: string) {
  const blok = (collector.x_studio_blok && collector.x_studio_blok[1]) || 'Hoofdgebouw';
  const verdieping = collector.x_studio_verdieping || '+00';
  const collectorName = collector.name.replace(/\s+/g, '-');
  return { projectName, blok, verdieping, collectorName };
}
